import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { Trans, useLingui } from '@lingui/react/macro';
import { useTheme } from '../contexts/ThemeContext';

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Public "Forgot password" page. Always shows the same neutral confirmation
 * whether or not an account exists for the entered email — account existence
 * is never revealed here (the email itself is the side channel).
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLingui();
  const { isDark } = useTheme();
  // Prefilled when the user arrives from the login form with a typed email.
  const [email, setEmail] = useState<string>((location.state as any)?.email || '');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const requestReset = async () => {
    setBusy(true);
    setError('');
    const result = await authService.forgotPassword(email.trim());
    setBusy(false);
    if (result.success) {
      setSubmitted(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setError(result.error || t`Something went wrong. Please try again.`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    await requestReset();
  };

  const trimmedEmail = email.trim();

  return (
    <div
      className="p-4 sm:p-8"
      style={{
        fontFamily: "'Inter', sans-serif",
        backgroundColor: 'var(--color-bg)',
        color: 'var(--color-text)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div className="max-w-md w-full">
        <div className="glass-panel rounded-3xl p-6 sm:p-8 border border-blue-100 dark:border-blue-900/30">
          <div className="text-center mb-6">
            <img
              src={isDark ? '/privacypal-logo-light.png' : '/privacypal-logo.png'}
              alt="PrivacyPal"
              className="w-48 sm:w-64 h-auto prism-edge mx-auto"
            />
          </div>

          {submitted ? (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-center"><Trans>Check your email</Trans></h2>
              <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg text-blue-800 dark:text-blue-300 text-sm">
                <p>
                  <Trans>
                    If an account exists for <strong>{trimmedEmail}</strong>, you'll receive a password
                    reset email shortly. The link expires in 20 minutes.
                  </Trans>
                </p>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                <Trans>Didn't get it? Check your spam folder, or resend below.</Trans>
              </p>
              <button
                type="button"
                onClick={requestReset}
                disabled={busy || cooldown > 0}
                className="w-full py-2 border border-slate-200 dark:border-slate-600 rounded-lg font-medium hover:bg-white dark:hover:bg-slate-700 transition-all disabled:opacity-50"
              >
                {busy ? t`Sending…` : cooldown > 0 ? t`Resend email (${cooldown}s)` : t`Resend email`}
              </button>
              {error && (
                <p className="text-sm text-center text-red-600 dark:text-red-400">{error}</p>
              )}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Trans>Back to sign in</Trans>
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-2 text-center"><Trans>Forgot your password?</Trans></h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
                <Trans>Enter your email address and we'll send you a link to reset it.</Trans>
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>Email</Trans></label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  disabled={busy}
                >
                  {busy ? t`Sending…` : t`Send reset link`}
                </button>
              </form>

              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Trans>Back to sign in</Trans>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
